import React, { useState } from 'react';
// import { useSelector } from 'react-redux';
import { Alert, Col, Row } from 'react-bootstrap';
import Headline from 'components/layout/Headline';
import FormInput from 'components/forms/FormInput';
import { getVariantCopyAmountValue } from 'helpers/cart';
// import { intlModule } from '@chedri/base';
import { legacyInjectIntl } from 'js/wrapper/injectIntl';

const quantityDisplay = ({ id, sizeWithQuantity, inputKey, isDisabled, maxAllowed, onChange }) => {
  const { quantity, translated, net_surcharge: netSurcharge } = sizeWithQuantity;

  return (
    <>
      <FormInput
        key={inputKey}
        prefixAddon={translated}
        name={id + '_selected'}
        value={Math.max(0, quantity || 0)}
        validation={{ valid: quantity >= 0, helpText: 'Must be greater than 0' }}
        onChange={(_, value) => onChange(id, +value)}
        type="number"
        min={0}
        max={maxAllowed}
        disabled={isDisabled}
      />
      {netSurcharge > 0 && <> Surcharge: {netSurcharge}</>}
    </>
  );
};

/**
 * Checks if an item is of type 'textile' based on its attributes.
 * Looks for an attribute named "Textile" and checks if its value is true (or evaluates to true).
 *
 * @param {object} item - The cart item's inner item object containing ident.
 * @returns {boolean} Returns true if the item is a textile, otherwise false.
 */
export function isTextileItem(item) {
  if (!item) {
    return false;
  }

  // Check directly on the item as well as in 'ident' for fallback
  const optionId = item.ident?.option_id;

  if (!optionId) {
    return false;
  }

  // If option_id is provided as an array
  if (Array.isArray(optionId)) {
    return optionId.includes('FA-112');
  }

  // If option_id is provided as a string (e.g., "FA-112" or comma-separated)
  if (typeof optionId === 'string') {
    return optionId.includes('FA-112');
  }

  return false;
}

const TextileSizesSelector = ({ cartItem, intl, changedCartItemValue }) => {
  // const messages = useSelector(intlModule.selectors.getMessages);
  const {
    textile_sizes_option: textileSizesOption,
    textile_sizes_selected: textileSizesSelected = [],
    variant,
  } = cartItem;

  const isTextile = isTextileItem(cartItem?.item);
  if (!isTextile && !textileSizesOption) {
    return null;
  }

  const totalNumberFromVariant = getVariantCopyAmountValue(variant);

  if (!totalNumberFromVariant || totalNumberFromVariant <= 0) {
    return null;
  }

  const availableSizes = textileSizesOption?.textile_sizes ?? [];
  if (!availableSizes.length) {
    return (
      <Row className="m-t-15">
        <Col>
          <Alert variant="warning">
            {intl.formatMessage(
              { id: 'textile_no_sizes_available', defaultMessage: 'No textile sizes available for selection or not configured.' }
            )}
          </Alert>
        </Col>
      </Row>
    );
  }

  const selectedDict = textileSizesSelected.reduce((acc, { textile_size_id: id, quantity }) => {
    acc[id] = quantity;

    return acc;
  }, {});

  const initialSelection = availableSizes.reduce((acc, size) => {
    const { textile_size_id: textileSizeId } = size;
    acc[textileSizeId] = { ...size, quantity: selectedDict[textileSizeId] || 0 };

    return acc;
  }, {});

  const [selectedSizeQuantities, setSelectedSizeQuantities] = useState(initialSelection);

  const sumQuantities = obj => Object.values(obj).reduce((acc, { quantity }) => acc + quantity, 0);
  const pickedNumber = sumQuantities(selectedSizeQuantities);

  const [inputKeys, setInputKeys] = useState({});

  const onChange = (id, quantity) => {
    const sizeWithQuantity = selectedSizeQuantities[id];

    // Calculate the sum of quantities for all *other* sizes (excluding the currently edited one)
    const otherSizesSum = Object.entries(selectedSizeQuantities)
      .filter(([key]) => key !== id)
      .reduce((acc, [, item]) => acc + (item.quantity || 0), 0);

    // Maximum allowed quantity for this specific size based on total limit
    const maxAllowedForThisSize = Math.max(0, totalNumberFromVariant - otherSizesSum);

    // Clamp the new quantity to the allowed range (0 to maxAllowedForThisSize)
    const clampedQuantity = Math.min(Math.max(quantity || 0, 0), maxAllowedForThisSize);

    // If the input was clamped or rejected, force a key update to reset the DOM input visually back to clampedQuantity
    if (quantity !== clampedQuantity) {
      setInputKeys(prev => ({
        ...prev,
        [id]: (prev[id] || 0) + 1,
      }));
    }

    const updatedSelectedSizeQuantities = {
      ...selectedSizeQuantities,
      [id]: { ...sizeWithQuantity, quantity: clampedQuantity },
    };

    setSelectedSizeQuantities(updatedSelectedSizeQuantities);

    const updatedPickedNumber = sumQuantities(updatedSelectedSizeQuantities);

    // Trigger update when the selected sum matches the total variant amount exactly
    if (updatedPickedNumber === totalNumberFromVariant) {
      const cartItemTextileSizesSelected = Object.values(updatedSelectedSizeQuantities)
        .filter(({ quantity }) => quantity > 0)
        .map(({ textile_size_id: id, quantity }) => ({
          textile_size_id: id,
          quantity,
        }));

      changedCartItemValue(cartItemTextileSizesSelected, 'textile_sizes_selected');
    }
  };

  const isLimitReached = pickedNumber >= totalNumberFromVariant;

  const list = Object.entries(selectedSizeQuantities).map(([id, sizeWithQuantity]) => {
    const currentQuantity = sizeWithQuantity.quantity || 0;

    // Calculate sum of other sizes to set max attribute for active fields
    const otherSizesSum = Object.entries(selectedSizeQuantities)
      .filter(([key]) => key !== id)
      .reduce((acc, [, item]) => acc + (item.quantity || 0), 0);
    const maxAllowed = Math.max(0, totalNumberFromVariant - otherSizesSum);

    // Disable field if total limit is reached and current field has 0 selected
    const isDisabled = isLimitReached && currentQuantity === 0;

    return (
      <Col xs={6} xxl={4} key={id}>
        {quantityDisplay({
          id,
          sizeWithQuantity,
          inputKey: `${id}_${inputKeys[id] || 0}`,
          isDisabled,
          maxAllowed,
          onChange,
        })}
      </Col>
    );
  });

  const title = `${textileSizesOption?.name} ${pickedNumber}/${totalNumberFromVariant}`;

  const messageId =
    pickedNumber === totalNumberFromVariant ? 'textile_exact_number_selected' : 'textile_select_exactly_n_items';
  const subtitle = intl.formatMessage({ id: messageId }, { total: totalNumberFromVariant });

  return (
    <Row className="cart-sizes-item">
      <Col>
        <Headline description={subtitle}>{title}</Headline>
        <Row>{list}</Row>
      </Col>
    </Row>
  );

  // // helper array-to-object { option_id: option }
  // const arrayToDictionary = (arr, propName) => Object.fromEntries(arr.map(option => [option[propName], option]));

  // const collectionDict = arrayToDictionary(collection, 'option_id');
  // const selectedDict = arrayToDictionary(selected ?? [], 'option_id');
  // const preselectedDict = arrayToDictionary(preselected ?? [], 'option_id');

  // const formRadios = collection.map(({ option_id: optionId, name, options }) => {
  //   const preselectedValue = preselectedDict[optionId];
  //   const preselectedValueId = preselectedValue?.option_value_id;
  //   const isPreselected = !!preselectedValue;
  //   const filterOptions = ({ option_value_id: valueId }) => !isPreselected || valueId === preselectedValueId;

  //   const listOptions = options.filter(filterOptions).map(({ option_value_id: valueId, name, price, currency }) => {
  //     const grossPrice = getGrossFromNet(price, cartItem.vat);
  //     const priceLabel = `${messages.net}: ${intl.formatNumber(price, { style: 'currency', currency })}
  //       / ${messages.gross}: ${intl.formatNumber(grossPrice, { style: 'currency', currency })}`;

  //     return {
  //       value: String(valueId),
  //       label: `${name} ${price ? priceLabel : ''}`,
  //     };
  //   });

  //   const optionValueDict = arrayToDictionary(options, 'option_value_id');
  //   const noneOptionId = options.find(({ price }) => !price)?.option_value_id ?? '';
  //   const value = String(selectedDict[optionId]?.option_value_id ?? noneOptionId);
  //   const onChange = (_, valueId) => {
  //     const value = optionValueDict[valueId];

  //     delete selectedDict[optionId];
  //     const selected = [...Object.values(selectedDict), value];

  //     changedCartItemValue(selected, 'perforation_options_selected');
  //   };

  //   return (
  //     <FormRadio
  //       name={`perforation_option_${optionId}}`}
  //       label={`${name} ${isPreselected ? `[${messages.perforation.preset}]` : ''}`}
  //       options={listOptions}
  //       onChange={isPreselected ? () => {} : onChange}
  //       value={value}
  //       key={optionId}
  //       disabled={isPreselected}
  //     />
  //   );
  // });

  // return (
  //   <>
  //     <Row>
  //       <Col>{formRadios}</Col>
  //     </Row>
  //   </>
  // );
};

export default legacyInjectIntl(TextileSizesSelector);
